import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheck, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./About.css";          // reuses all About styles — no new CSS needed
import "./MissionVision.css";  // small overrides / additions

/* ── hero background — reuse same hero as About ── */
import heroBg from "../assets/images/ppt_media_image20.jpeg";

/* ── intro image ── */
import introImg from "../assets/images/bakwenakit.png";

/* ── intro corner icon ── */
import iconLogo3 from "../assets/images/about/icon-logo3.png";

/* ── background video ── */
import bgVideo from "../assets/images/videos/landscape_video8.mp4";

/* ── tab panel images ── */
import tabImg1 from "../assets/images/about/ppt_media_image22.jpg";
import tabImg2 from "../assets/images/about/ppt_media_image18.jpg";
import tabImg3 from "../assets/images/about/ppt_media_image17.jpg";

/* ── team photo ── */
import mdPhoto from "../assets/images/about/managing-director.png";

/* ── disable right-click ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ── animated counter ── */
function Counter({ to, suffix = "", delay = 0 }) {
  const [ref, inView] = useInView();
  const el = useRef(null);
  useEffect(() => {
    if (!inView) return;
    let start = null;
    const run = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 1800, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      if (el.current) el.current.textContent = Math.floor(eased * to);
      if (p < 1) requestAnimationFrame(run);
    };
    setTimeout(() => requestAnimationFrame(run), delay);
  }, [inView, to, delay]);
  return (
    <span ref={ref}>
      <span ref={el}>0</span>{suffix}
    </span>
  );
}

/* ── stats ── */
const stats = [
  { to: 140, suffix: "",  lbl: "Completed Projects" },
  { to: 99,  suffix: "%", lbl: "Happy Clients"      },
  { to: 35,  suffix: "+", lbl: "Expert Engineers"   },
];

/* ── team ── */
const team = [
  {
    name:  "Tshepo Moepya",
    role:  "Managing Director",
    tel:   "012 004-1000",
    cell:  "(072) 623-4209",
    email: "Tshepo.Moepya@bakwenakit.co.za",
    photo: mdPhoto,
  },
];

/* ── tabbed info panel — Mission & Vision content ── */
const vidTabs = [
  {
    label: "Who We Are",
    img: tabImg1,
    title: "Who We Are",
    bullets: [
      "The Bakwena Kit brand is one that embodies the aspiration that we have as a Proudly South African enterprise.",
      "A new culture. It is about being passionate and ready to serve our customers in a new uncontested way.",
      "And yes, we are excited about being proudly South African.",
    ],
  },
  {
    label: "Vision & Mission",
    img: tabImg2,
    title: "Vision & Mission",
    bullets: [
      "Our vision is to improve the standard of support, engineering input and construction experience in both the civil, mining and building environment.",
      "Our mission is to continually strive towards innovative solutions, economical services, efficient project execution, unrivalled customer experience, and becoming the preferred temporary works service provider and construction partner.",
      "We aim to be celebrated for contributing valuable insight and unrivalled expertise to the South African construction industry, as well as to be acknowledged for nurturing coming leaders who will sustain the African economy.",
    ],
  },
  {
    label: "Our Values",
    img: tabImg3,
    title: "Our Values",
    bullets: [
      "Customer focus – building an open relationship with our clients, installing trust through teamwork and mutual relationships.",
      "Integrity – doing what is right and delivering on our commitments.",
      "Continuous improvement – learning from our mistakes to ensure improved results.",
    ],
  },
];

/* ════════════════════════════════════════════════════════════ */
export default function MissionVision() {
  useNoContextMenu();

  const [heroRef,  heroInView]  = useInView();
  const [introRef, introInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [vidRef,   vidInView]   = useInView();
  const [teamRef,  teamInView]  = useInView();

  const videoRef   = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    vid.play().catch(() => {});
  }, []);

  const tab = vidTabs[activeTab];

  return (
    <main className="ab">

      {/* ══════ 1. HERO ══════ */}
      <section className="ab-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="ab-hero__overlay" />
        <div className="container ab-hero__body" ref={heroRef}>
          <div className={`ab-hero__label fade-in ${heroInView ? "visible" : ""}`}>
            MISSION &amp; VISION
          </div>
          <h1
            className={`ab-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            DRIVEN BY PURPOSE,<br />GUIDED BY VALUES
          </h1>
        </div>
        <div className="ab-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <Link to="/about">About</Link>
          <FiArrowRight size={13} />
          <span>Mission &amp; Vision</span>
        </div>
      </section>

      {/* ══════ 2. INTRO — Bakwena Kit Profile ══════ */}
      <section className="ab-intro" ref={introRef}>
        <div className="container ab-intro__inner">
          <div className={`ab-intro__img-col fade-in ${introInView ? "visible" : ""}`}>
            <img
              className="ab-intro__img-main"
              src={introImg}
              alt="Bakwena Kit"
              loading="lazy"
              draggable="false"
              onDragStart={e => e.preventDefault()}
            />
            <div className="ab-intro__img-accent" aria-hidden="true" />
            <div className="ab-intro__icon-badge">
              <img
                src={iconLogo3}
                alt="Bakwena Kit logo"
                draggable="false"
                onDragStart={e => e.preventDefault()}
              />
            </div>
          </div>

          <div
            className={`ab-intro__right fade-in ${introInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.15s" }}
          >
            <div className="ab-intro__tag">BAKWENA KIT PROFILE</div>
            <h2 className="ab-intro__h2">
              Our Mission, Vision<br /><span>&amp; Core Values</span>
            </h2>

            <div className="ab-intro__quote">
              <div className="ab-intro__bar" />
              <p>
                Bakwena Kit (Pty) Ltd is a proudly South African formwork and scaffolding company.
                Our mission is to elevate the standard of support services and engineering expertise,
                positioning Bakwena Kit as a leading provider of temporary works solutions in both
                civil and building sectors.
              </p>
            </div>

            <div className="ab-intro__block">
              <div className="ab-intro__block-label">OUR VISION</div>
              <p className="ab-intro__block-text">
                "To improve the standard of support, engineering input and construction experience
                in both the civil, mining and building environment."
              </p>
            </div>

            <div className="ab-intro__block">
              <div className="ab-intro__block-label">OUR MISSION</div>
              <p className="ab-intro__block-text">
                "To continually strive towards innovative solutions, economical services, efficient
                project execution, unrivalled customer experience, and becoming the preferred
                temporary works service provider and construction partner."
              </p>
            </div>

            <Link to="/contact" className="ab-intro__cta">
              Get In Touch <FiArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════ 3. STATS ══════ */}
      <section className="ab-stats" ref={statsRef}>
        <div className="ab-stats__inner container">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`ab-stat fade-in ${statsInView ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <span className="ab-stat__val">
                <Counter to={s.to} suffix={s.suffix} delay={i * 120} />
              </span>
              <span className="ab-stat__lbl">{s.lbl}</span>
              <span className="ab-stat__line" />
            </div>
          ))}
        </div>
      </section>

      {/* ══════ 4. VIDEO + TAB BOX ══════ */}
      <section className="ab-video" ref={vidRef}>
        <div className="ab-video__bg">
          <video ref={videoRef} src={bgVideo} autoPlay muted loop playsInline preload="auto" />
        </div>
        <div className="ab-video__overlay" aria-hidden="true" />

        <div className={`container ab-video__headline fade-in ${vidInView ? "visible" : ""}`}>
          <div className="ab-video__left">
            <div className="ab-video__tag">OUR PURPOSE</div>
            <h2 className="ab-video__h2">
              BUILT ON ETHICS,<br /><span>DRIVEN BY TRUST</span>
            </h2>
            <p className="ab-video__sub">
              "We will build the future of this business on ethics and trust — establishing
              an unprecedented customer experience."
            </p>
          </div>
        </div>
        <div className="ab-video__stripe" aria-hidden="true" />

        {/* Tab box — overlaps the bottom third of the video */}
        <div className="ab-vid-box__anchor">
          <div className="container ab-vid-box__wrapper">
            <div
              className={`ab-vid-box fade-in ${vidInView ? "visible" : ""}`}
              style={{ transitionDelay: "0.2s" }}
            >
              <div className="ab-vid-box__tabs">
                {vidTabs.map((t, i) => (
                  <button
                    key={i}
                    className={`ab-vid-box__tab${activeTab === i ? " active" : ""}`}
                    onClick={() => setActiveTab(i)}
                    type="button"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="ab-vid-box__body">
                <div className="ab-vid-box__img-wrap">
                  <img
                    src={tab.img}
                    alt={tab.title}
                    loading="lazy"
                    draggable="false"
                    onDragStart={e => e.preventDefault()}
                  />
                </div>
                <div className="ab-vid-box__content">
                  <h3 className="ab-vid-box__title">{tab.title}</h3>
                  <ul className="ab-vid-box__list">
                    {tab.bullets.map((b, bi) => (
                      <li key={bi}>
                        <span className="ab-vid-box__check" aria-hidden="true">
                          <FiCheck size={11} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ 5. OUR PEOPLE — moved here in place of values grid ══════ */}
      <section className="ab-team mv-team-offset" ref={teamRef}>
        <div className="container">
          <div className={`ab-team__head fade-in ${teamInView ? "visible" : ""}`}>
            <div className="ab-team__tag">OUR PEOPLE</div>
            <h2 className="ab-team__h2">Meet the Team Behind Every Build</h2>
            <p className="ab-team__sub">
              Skilled engineers, site managers, and safety specialists who bring your projects
              to life — safely, on time, and to the highest standards.
            </p>
          </div>
          <div className="ab-team__grid ab-team__grid--single">
            {team.map((m, i) => (
              <div
                key={i}
                className={`ab-team-card fade-in ${teamInView ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div className="ab-team-card__photo">
                  <img
                    src={m.photo}
                    alt={m.name}
                    loading="lazy"
                    draggable="false"
                    onDragStart={e => e.preventDefault()}
                  />
                </div>
                <div className="ab-team-card__body">
                  <div className="ab-team-card__name">{m.name}</div>
                  <div className="ab-team-card__role">{m.role}</div>
                  <div className="ab-team-card__contact">
                    <span>{m.tel}</span>
                    <span>{m.cell}</span>
                    <a href={`mailto:${m.email}`}>{m.email}</a>
                  </div>
                </div>
                <div className="ab-team-card__bar" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ 6. AIM CALLOUT ══════ */}
      <section className="mv-aim">
        <div className="container mv-aim__inner">
          <div className="mv-aim__tag">OUR AIM</div>
          <blockquote className="mv-aim__quote">
            "We aim to be celebrated for contributing valuable insight and unrivalled expertise
            to the South African construction industry, as well as to be acknowledged for nurturing
            coming leaders who will sustain the African economy."
          </blockquote>
          <Link to="/contact" className="ab-intro__cta mv-aim__cta">
            Work With Us <FiArrowRight size={14} />
          </Link>
        </div>
      </section>

    </main>
  );
}